import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { AxiosError } from 'axios'
import { MenuItem, TextFieldProps, Typography } from '@mui/material'
import useLiveListFilterBuilder from '@/hooks/useLiveListFilterBuilder'
import { useGetIntegrationLiveListTypes } from '@/service-library/hooks/integration-live-list-types'
import { useGetLiveListFilterData } from '@/services/hooks/relay/dynamic'
import { DocumentRow } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import { LiveListEntity } from '@/types/live-lists'
import { getFieldsFromProject } from '@/utils/field'
import { decodeHtml } from '@/utils/text'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useDocumentRowsContext } from '../providers/DocumentRowsProvider'
import { useDocumentRowValuesContext } from '../providers/DocumentRowValuesProvider'
import { useDocumentsChangeSetsContext } from '../providers/DocumentsChangeSetsProvider'
import useGetValueData from '../useGetValueData'
import BasePickerField from './BasePickerField'

type LiveListPickerFieldProps = {
  onChange: (
    newValue: string | null,
    refId: string | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    jsonObject?: Record<string, any> | null,
  ) => void
  field: ProjectGridField
  value: LiveListEntity | null
  setDisableArrowKeys?: Dispatch<SetStateAction<boolean>>
  isInTable?: boolean
  documentRow: DocumentRow
} & Omit<TextFieldProps, 'onChange' | 'value'>

export const liveListNoneOption: LiveListEntity = {
  entity_id: 'None',
  entity: {},
  pd_display_name: 'None',
  pd_display_detail: '',
  pd_group_value: undefined,
  pd_address: null,
}

export default function LiveListPickerField({
  disabled,
  field,
  value,
  label,
  onChange,
  setDisableArrowKeys = () => {},
  helperText,
  documentRow,
  ...props
}: LiveListPickerFieldProps) {
  const { project, fieldsMap } = useProjectContext()
  const { isFieldUpdating } = useDocumentsChangeSetsContext()
  const { baseRow } = useDocumentRowsContext()

  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(
    decodeHtml(value?.pd_display_name) || '',
  )

  const [enabled, setEnabled] = useState(false)
  const [inputValue, setInputValue] = useState(
    decodeHtml(value?.pd_display_name) || '',
  )
  const [liveListHelperText, setLiveListHelperText] = useState<
    string | undefined
  >(undefined)

  const { document } = useDocumentContext()
  const getValueData = useGetValueData()
  const { getFieldDocumentRowValue } = useDocumentRowValuesContext()

  const { integrationLiveListTypes } = useGetIntegrationLiveListTypes({
    filters: {
      org_credential_type_id: project?.org_credential_type_id || '',
      data_format: 'live-list-entity',
      info_type_hints__id: field.info_type_id || '',
      fields__include: 'dependent_info_types_ids',
    },
  })

  const { dependentData, dependentFields } = useMemo(() => {
    const dependentInfoTypesIds = new Set(
      integrationLiveListTypes.flatMap((type) => type.dependent_info_types_ids),
    )
    const dependentFields = getFieldsFromProject(project).filter((field) =>
      dependentInfoTypesIds.has(field.info_type_id || ''),
    )

    if (!dependentFields?.length)
      return { dependentData: undefined, dependentFields: undefined }

    const tempData: ReturnType<typeof getValueData>['value'][] = []
    const tempDependentFields: { fieldId: string; rowId: string | null }[] = []
    dependentFields.forEach((depField) => {
      const rowId = fieldsMap[depField.id].parentField
        ? documentRow.id
        : baseRow?.id
      const value = getValueData({
        fieldId: depField.id,
        rowId,
      }).value
      tempData.push(value)
      tempDependentFields.push({ fieldId: depField.id, rowId: rowId || null })
    })

    return {
      dependentData: tempData,
      dependentFields: tempDependentFields,
    }
  }, [
    baseRow?.id,
    documentRow.id,
    fieldsMap,
    getValueData,
    integrationLiveListTypes,
    project,
  ])

  const dependentFieldsAreUpdating = useMemo(() => {
    if (!dependentFields?.length) return false
    return dependentFields.some(({ fieldId, rowId }) =>
      isFieldUpdating(fieldId, rowId),
    )
  }, [dependentFields, isFieldUpdating])

  const extra = useMemo(
    () => ({
      org_id: document?.owner_org_id,
      org_credential_type_id: project?.org_credential_type_id || undefined,
      doc_id: document?.id,
      doc_row_id: documentRow.id,
      query: searchValue,
      // Not a real filter, but helps with caching when dependent field values change
      dependentData: dependentData ? dependentData.join(',') : undefined,
    }),
    [
      document?.owner_org_id,
      document?.id,
      project?.org_credential_type_id,
      documentRow.id,
      searchValue,
      dependentData,
    ],
  )

  const { liveListUrl, domainFilters } = useLiveListFilterBuilder({
    storedFilter: field.live_list_filter_config,
    infoTypeId: field.info_type_id,
    document,
    documentRow,
    getFieldDocumentRowValue,
    extra,
  })

  const {
    allData,
    hasNextPage,
    fetchNextPage,
    isPending,
    isFetching,
    error,
    isSuccess,
  } = useGetLiveListFilterData({
    liveListUrl,
    domainFilters,
    // Using `enabled` when no input value, so we don't have to refetch every time the user opens the list.
    // Can't use `enabled` when there is an input value, because on closing the search value changes to an empty string,
    // the filter (querykey) changes, and it refetches even when closed, and then, once again, when the search value changes to the input value.
    enabled:
      (inputValue ? open : enabled) &&
      !!project.org_credential_type_id &&
      integrationLiveListTypes.length > 0,
    retry: (failureCount, error) => {
      const status = (error as AxiosError).response?.status
      if (status && [400, 422].includes(status)) return false
      return failureCount < 3
    },
  })

  const options = useMemo(() => {
    if (isPending) return []
    if (!searchValue || 'none'.includes(searchValue.trim().toLowerCase())) {
      return [liveListNoneOption, ...allData]
    }
    return allData
  }, [allData, isPending, searchValue])

  useEffect(() => {
    if (error) {
      const { response } = error as AxiosError
      const status = response?.status
      const code = (response?.data as unknown as Record<string, string>)?.code
      if (status === 400) {
        setLiveListHelperText(
          'Unable to retrieve items. Please try again later.',
        )
      } else if (status === 422 && code === 'missing_credentials') {
        setLiveListHelperText(
          "Unable to retrieve items. Are there proper credentials for this document's organization?",
        )
      } else if (status === 422 && code === 'invalid_credentials') {
        setLiveListHelperText(
          "Unable to retrieve items. Are the credentials for this document's organization valid?",
        )
      }
    } else if (isSuccess) {
      setLiveListHelperText(undefined)
    }
  }, [error, isSuccess])

  return (
    <BasePickerField<LiveListEntity>
      value={value}
      label={label}
      disabled={
        disabled ||
        !project?.org_credential_type_id ||
        integrationLiveListTypes.length === 0 ||
        dependentFieldsAreUpdating ||
        dependentData?.some((data) => !data)
      }
      enabled={enabled}
      setEnabled={setEnabled}
      inputValue={inputValue}
      setInputValue={setInputValue}
      onChange={(newValue) => {
        setOpen(false)
        onChange(newValue?.pd_display_name || '', null, newValue)
      }}
      options={options as LiveListEntity[]}
      groupBy={
        // Need to look at the second option since the first is always "None"
        options.length > 1 && options[1].pd_group_value
          ? (option) => option.pd_group_value || 'None'
          : undefined
      }
      open={open}
      setOpen={setOpen}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isFetching={isFetching}
      getOptionLabel={(option) => decodeHtml(option.pd_display_name) || ''}
      isOptionEqualToValue={(option, value) =>
        option?.entity_id === value.entity_id
      }
      isLoading={isPending}
      renderOption={(props, option) => {
        return (
          <MenuItem
            {...props}
            key={props.id}
            value={option.entity_id}
            sx={{ alignItems: 'center' }}
          >
            {decodeHtml(option.pd_display_name)}
            {option.pd_display_detail && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, mt: '1px' }}
              >
                {option.pd_display_detail}
              </Typography>
            )}
          </MenuItem>
        )
      }}
      setDisableArrowKeys={setDisableArrowKeys}
      helperText={liveListHelperText || helperText}
      {...props}
    />
  )
}
